import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Card } from '@/shared/components/ui/card'
import { Search, X } from 'lucide-react'
import type { UsersFilters } from '@/modules/users/types/user'

interface UsersFiltersProps {
  filters: UsersFilters
  onFiltersChange: (filters: UsersFilters) => void
  onClearFilters: () => void
}

export function UsersFilters({ filters, onFiltersChange, onClearFilters }: UsersFiltersProps) {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined, page: 1 })
  }

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      is_active: value === 'all' ? undefined : value === 'active',
      page: 1,
    })
  }

  const handleRoleChange = (value: string) => {
    onFiltersChange({
      ...filters,
      is_staff: value === 'all' ? undefined : value === 'staff',
      page: 1,
    })
  }

  const hasActiveFilters =
    filters.search ||
    filters.is_active !== undefined ||
    filters.is_staff !== undefined ||
    filters.group_id !== undefined

  return (
    <Card className="p-4 border border-gray-300 dark:border-gray-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, email o información..."
              value={filters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Status Filter */}
          <Select
            value={
              filters.is_active === undefined
                ? 'all'
                : filters.is_active
                  ? 'active'
                  : 'inactive'
            }
            onValueChange={handleStatusChange}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
            </SelectContent>
          </Select>

          {/* Role Filter */}
          <Select
            value={
              filters.is_staff === undefined ? 'all' : filters.is_staff ? 'staff' : 'regular'
            }
            onValueChange={handleRoleChange}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Rol" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="regular">Usuario regular</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="default"
              onClick={onClearFilters}
              className="whitespace-nowrap"
            >
              <X className="mr-2 h-4 w-4" />
              Limpiar
            </Button>
          )}
        </div>

        {/* Active Filters Chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
            <span>Filtros activos:</span>
            {filters.search && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 dark:text-teal-400">
                Búsqueda: "{filters.search}"
              </span>
            )}
            {filters.is_active !== undefined && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 dark:text-teal-400">
                Estado: {filters.is_active ? 'Activo' : 'Inactivo'}
              </span>
            )}
            {filters.is_staff !== undefined && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 dark:text-teal-400">
                Rol: {filters.is_staff ? 'Staff' : 'Usuario regular'}
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
